import React, { useEffect } from 'react';
import {
  PDFViewer,
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Image,
} from '@react-pdf/renderer';
import cameraWeightReportStyles from 'src/components/WeightReport/CameraWeightReportStyles';
import {
  getCameraWeightReport,
  getCameraWeightReportForVehicleTrackingLoad,
} from 'src/redux/Actions/VehicleTracking';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import PlaceholderImage from 'src/Images/PlaceholderImage.png';
import { useParams } from 'react-router-dom';

export default function PrintCameraWeightReport({
  setShowCameraWeightReport,
  vtLoadHeaderUniqueNumber,
}) {
  const dispatch = useDispatch();
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const { vehicleTrackingNumber } = useParams();

  const { cameraWeightReportData } = vehicleTrackingDataReducer;
  const reportDateTime = new Date();
  const direction = cameraWeightReportData?.direction;
  const packTypes = cameraWeightReportData?.packTypes || [];
  const totalPackQuantity = cameraWeightReportData?.packQuantities?.reduce(
    (total, quantity) => total + quantity,
    0,
  );
  const isLoadCameraWeightReport = Boolean(vtLoadHeaderUniqueNumber);
  const isOutwardVehicleTracking = Number(direction) === 2;
  const firstBusinessPartnerName =
    cameraWeightReportData?.documentData?.[0]?.business_partner_name;

  const outwardPartyName =
    cameraWeightReportData?.documentData?.length > 1
      ? 'Multiple Customer'
      : cameraWeightReportData?.documentData?.[0]?.business_partner_name;

  const totalQuantityDisplay = isLoadCameraWeightReport
    ? `${cameraWeightReportData?.totalQuantity || ' '} ${cameraWeightReportData?.totalQuantityUom || ' '}`
    : isOutwardVehicleTracking
      ? `${cameraWeightReportData?.totalGrossWeight || ' '} KG`
      : `${cameraWeightReportData?.totalQuantity || ' '} ${cameraWeightReportData?.totalQuantityUom || ' '}`;

  const getAttachmentImageSrc = (attachment) => {
    if (typeof attachment === 'string') return attachment;
    return attachment?.url || PlaceholderImage;
  };

  const getAttachmentKey = (attachment, index) => {
    if (typeof attachment === 'string') return attachment;
    return attachment?.id || attachment?.url || index;
  };

  useEffect(() => {
    if (!organisationId || !vehicleTrackingNumber) return;

    if (vtLoadHeaderUniqueNumber) {
      dispatch(
        getCameraWeightReportForVehicleTrackingLoad(
          organisationId,
          vehicleTrackingNumber,
          vtLoadHeaderUniqueNumber,
        ),
      );
      return;
    }

    dispatch(getCameraWeightReport(organisationId, vehicleTrackingNumber));
  }, [
    dispatch,
    organisationId,
    vehicleTrackingNumber,
    vtLoadHeaderUniqueNumber,
  ]);

  const cameraWeightReportDocument = (
    <Document>
      <Page size="A4" style={cameraWeightReportStyles.page} key={0}>
        <View style={cameraWeightReportStyles.justifyCenter}>
          <View style={cameraWeightReportStyles.reportContainer}>
            <View style={cameraWeightReportStyles.justifyCenter}>
              <Text
                style={[
                  cameraWeightReportStyles.textBold,
                  cameraWeightReportStyles.textLg,
                ]}
              >
                {cameraWeightReportData?.displayName}
              </Text>
            </View>
            <View style={cameraWeightReportStyles.justifyCenter}>
              <Text
                style={[
                  cameraWeightReportStyles.textSemiBold,
                  cameraWeightReportStyles.textSm,
                ]}
              >
                {cameraWeightReportData?.tenantAddress}
              </Text>
            </View>
          </View>
        </View>
        <View style={cameraWeightReportStyles.justifyEnd}>
          <View style={cameraWeightReportStyles.reportField}>
            <Text
              style={[
                cameraWeightReportStyles.textBold,
                cameraWeightReportStyles.textSm,
              ]}
            >
              Print Date :
            </Text>
            <Text style={cameraWeightReportStyles.textSm}>
              {reportDateTime.toLocaleDateString('en-GB')}{' '}
              {reportDateTime.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </View>
        </View>
        <View style={cameraWeightReportStyles.borderBlack} />
        <View style={cameraWeightReportStyles.reportDetail}>
          <View style={cameraWeightReportStyles.col6}>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Ticket No
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  :{cameraWeightReportData?.vehicleTrackingCode}
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Party Name
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  :&nbsp;
                  {isOutwardVehicleTracking
                    ? outwardPartyName
                    : firstBusinessPartnerName}
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Vehicle No
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  : {cameraWeightReportData?.vehicleNumber}
                </Text>
              </View>
            </View>
            {isOutwardVehicleTracking && (
              <View style={cameraWeightReportStyles.reportDetail}>
                <View style={cameraWeightReportStyles.col6}>
                  <Text
                    style={[
                      cameraWeightReportStyles.textSemiBold,
                      cameraWeightReportStyles.textSm,
                    ]}
                  >
                    Pack Type
                  </Text>
                </View>
                <View style={cameraWeightReportStyles.col6}>
                  <Text style={cameraWeightReportStyles.textSm}>
                    :&nbsp;
                    {Array.isArray(packTypes)
                      ? packTypes.join(', ')
                      : packTypes || ' '}
                  </Text>
                </View>
              </View>
            )}

            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Gross Weight
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  : {cameraWeightReportData?.grossWeight || 0} KG
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Tare Weight
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  : {cameraWeightReportData?.tareWeight || 0} KG
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Net Weight
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  : {cameraWeightReportData?.netWeight || 0} KG
                </Text>
              </View>
            </View>
          </View>
          <View style={cameraWeightReportStyles.col6}>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Supplier Name
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>:</Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Material Name
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  :&nbsp;
                  {cameraWeightReportData?.materialNames
                    ?.slice(0, 4)
                    .map((materialName) => materialName)
                    .join(', ')}
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  Total Quantity
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  :&nbsp;{totalQuantityDisplay}
                </Text>
              </View>
            </View>
            {isOutwardVehicleTracking && (
              <View style={cameraWeightReportStyles.reportDetail}>
                <View style={cameraWeightReportStyles.col6}>
                  <Text
                    style={[
                      cameraWeightReportStyles.textSemiBold,
                      cameraWeightReportStyles.textSm,
                    ]}
                  >
                    Pack Quantity
                  </Text>
                </View>
                <View style={cameraWeightReportStyles.col6}>
                  <Text style={cameraWeightReportStyles.textSm}>
                    :&nbsp;
                    {totalPackQuantity ?? ' '}
                  </Text>
                </View>
              </View>
            )}
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  {cameraWeightReportData?.grossWeightDate || ' '}
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  {cameraWeightReportData?.grossWeightTime || ' '}
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  {cameraWeightReportData?.tareWeightDate || ' '}
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  {cameraWeightReportData?.tareWeightTime || ' '}
                </Text>
              </View>
            </View>
            <View style={cameraWeightReportStyles.reportDetail}>
              <View style={cameraWeightReportStyles.col6}>
                <Text
                  style={[
                    cameraWeightReportStyles.textSemiBold,
                    cameraWeightReportStyles.textSm,
                  ]}
                >
                  User Name
                </Text>
              </View>
              <View style={cameraWeightReportStyles.col6}>
                <Text style={cameraWeightReportStyles.textSm}>
                  :{cameraWeightReportData?.userName?.name}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <View style={cameraWeightReportStyles.borderBlack} />
        <View style={cameraWeightReportStyles.reportContainer}>
          <View style={cameraWeightReportStyles.reportContainer}>
            <Text
              style={[
                cameraWeightReportStyles.textBold,
                cameraWeightReportStyles.textSm,
                cameraWeightReportStyles.underline,
              ]}
            >
              1ST WEIGHMENTS
            </Text>
            <View style={cameraWeightReportStyles.reportDetail}>
              {cameraWeightReportData?.weightBridgeIn?.map(
                (attachment, index) => (
                  <View
                    key={getAttachmentKey(attachment, index)}
                    style={cameraWeightReportStyles.col6}
                  >
                    <Image
                      src={getAttachmentImageSrc(attachment)}
                      style={cameraWeightReportStyles.image200}
                    />
                  </View>
                ),
              )}
            </View>
          </View>
          <View style={cameraWeightReportStyles.reportContainer}>
            <Text
              style={[
                cameraWeightReportStyles.textBold,
                cameraWeightReportStyles.textSm,
                cameraWeightReportStyles.underline,
              ]}
            >
              2ND WEIGHMENTS
            </Text>
            <View style={cameraWeightReportStyles.reportDetail}>
              {cameraWeightReportData?.weightBridgeOut?.map(
                (attachment, index) => (
                  <View
                    key={getAttachmentKey(attachment, index)}
                    style={cameraWeightReportStyles.col6}
                  >
                    <Image
                      src={getAttachmentImageSrc(attachment)}
                      style={cameraWeightReportStyles.image200}
                    />
                  </View>
                ),
              )}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  return (
    <div id="quotation-content">
      <div className="div-flex-row py-1 px-2 div-space-between">
        <button className="btn-black">
          <PDFDownloadLink
            document={cameraWeightReportDocument}
            fileName="temp.pdf"
            className="text-white"
          >
            {({ loading }) => (loading ? 'Loading document...' : 'Download')}
          </PDFDownloadLink>
        </button>
        <Close onClick={() => setShowCameraWeightReport(false)} />
      </div>
      <PDFViewer style={{ width: '100%', height: '100vh' }}>
        {cameraWeightReportDocument}
      </PDFViewer>
    </div>
  );
}
